import { useState, useEffect, useCallback, useRef } from 'react';
import {
    LoginRequest,
    LoginResponse,
    RefreshTokenResponse,
    CurrentUserResponse,
    AuthState,
    AuthError,
    AuthTokens,
    TeamModel
} from '../types/auth';
import { authApiService, AuthApiResponse } from '../services/authApiService';
import { debugLog, debugError, debugWarn } from '../utils/debug';

export interface UseAuthState extends AuthState {
    isLoggingOut?: boolean;
}

export interface UseAuthActions {
    login: (credentials: LoginRequest) => Promise<void>;
    logout: () => Promise<void>;
    refreshTokenAction: () => Promise<void>;
    getCurrentUser: () => Promise<void>;
    clearError: () => void;
    checkAuthStatus: () => Promise<void>;
    updateUserTeam: (teamUpdate: (team: TeamModel | undefined) => TeamModel | undefined) => void;
    switchTeam: (teamId: string) => Promise<void>;
}

export interface UseAuthReturn extends UseAuthState, UseAuthActions { }

// Module-level ref to persist across remounts of the hook
const moduleHasCheckedAuthRef = { current: false };
const isLoggingOutRef = { current: false };

/**
 * Custom hook for managing authentication state and operations
 * @returns Object containing auth state and actions
 */
export function useAuth(): UseAuthReturn {
    const [state, setState] = useState<AuthState>(() => {
        // Try to restore auth state from sessionStorage on mount
        // But keep loading true until we verify the token is still valid
        try {
            const saved = sessionStorage.getItem('auth_state');
            if (saved) {
                const parsed = JSON.parse(saved);
                // Only restore if we have a user and it's authenticated
                if (parsed.isAuthenticated && parsed.user) {
                    // debugLog('[useAuth] Found session auth in initial state, will verify token');
                    // Don't set moduleHasCheckedAuthRef here - let useEffect verify token first
                    return {
                        ...parsed,
                        // Set token placeholders since we use HTTP-only cookies
                        // The actual tokens are in HTTP-only cookies, not in sessionStorage
                        accessToken: 'http-only-cookie',
                        refreshToken: 'http-only-cookie',
                        loading: true, // Keep loading true until we verify token is valid
                    };
                }
            }
        } catch (e) {
            debugWarn('[useAuth] Failed to restore auth state:', e);
        }
        return {
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            loading: true, // Start with loading true to check auth status
            error: null,
        };
    });

    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hasCheckedAuthRef = useRef<boolean>(moduleHasCheckedAuthRef.current);
    const checkInProgressRef = useRef<boolean>(false);
    const lastValidatedStateRef = useRef<string | null>(null); // Track last validated state to prevent duplicate validations
    const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

    // Sync module ref with instance ref
    if (moduleHasCheckedAuthRef.current && !hasCheckedAuthRef.current) {
        hasCheckedAuthRef.current = true;
    }

    // Log when hook is initialized/unmounted (only in development)
    useEffect(() => {
        // debugLog('[useAuth] Hook initialized/mounted', {
        //     timestamp: new Date().toISOString(),
        //     hasCheckedRef: hasCheckedAuthRef.current,
        //     moduleChecked: moduleHasCheckedAuthRef.current,
        //     isAuthenticated: state.isAuthenticated,
        //     hasUser: !!state.user,
        //     sessionStorage: (() => {
        //         try {
        //             const saved = sessionStorage.getItem('auth_state');
        //             return saved ? 'exists' : 'missing';
        //         } catch {
        //             return 'error';
        //         }
        //     })()
        // });

        return () => {
            // debugLog('[useAuth] Hook unmounting/cleanup', {
            //     timestamp: new Date().toISOString(),
            //     wasAuthenticated: state.isAuthenticated
            // });
        };
    }, []); // Only run on mount/unmount

    // Debug: Log when state changes (only in development)
    useEffect(() => {
        // debugLog('[useAuth] State changed:', {
        //     isAuthenticated: state.isAuthenticated,
        //     hasUser: !!state.user,
        //     loading: state.loading,
        //     hasCheckedRef: hasCheckedAuthRef.current,
        //     checkInProgress: checkInProgressRef.current
        // });
    }, [state.isAuthenticated, state.user, state.loading]);

    const clearError = useCallback(() => {
        setState(prev => ({ ...prev, error: null }));
    }, []);

    const setLoading = useCallback((loading: boolean) => {
        setState(prev => ({ ...prev, loading }));
    }, []);

    const setError = useCallback((error: string | null) => {
        setState(prev => ({ ...prev, error, loading: false }));
    }, []);

    const setAuthData = useCallback((authData: {
        user: CurrentUserResponse;
        tokens: AuthTokens;
    }) => {
        const newState = {
            isAuthenticated: true,
            user: authData.user,
            accessToken: authData.tokens.accessToken,
            refreshToken: authData.tokens.refreshToken,
            loading: false,
            error: null,
        };
        setState(prev => ({
            ...prev,
            ...newState,
        }));
        // Persist to sessionStorage
        try {
            sessionStorage.setItem('auth_state', JSON.stringify({
                isAuthenticated: newState.isAuthenticated,
                user: newState.user,
            }));
            moduleHasCheckedAuthRef.current = true;
        } catch (e) {
            debugWarn('[useAuth] Failed to persist auth state:', e);
        }
    }, []);

    const clearAuthData = useCallback(() => {
        // Clear React state
        setState(prev => ({
            ...prev,
            isAuthenticated: false,
            user: null,
            accessToken: null,
            refreshToken: null,
            loading: false,
            error: null,
        }));

        // Clear from sessionStorage
        try {
            sessionStorage.removeItem('auth_state');
            // Clear any redirect paths that might be stored
            sessionStorage.removeItem('redirectAfterLogin');
            moduleHasCheckedAuthRef.current = false;
        } catch (e) {
            debugWarn('[useAuth] Failed to clear auth state from sessionStorage:', e);
        }

        // Clear any localStorage items that might contain user-specific data
        // Note: We don't clear all localStorage as it may contain non-auth preferences
        // Only clear items that are clearly auth-related
        try {
            // Clear any user-specific localStorage items if they exist
            // Add any other auth-related localStorage keys here as needed
            const authRelatedKeys = [
                'auth_state', // In case it was stored in localStorage instead of sessionStorage
                'user_preferences', // If you have user-specific preferences
            ];
            authRelatedKeys.forEach(key => {
                try {
                    localStorage.removeItem(key);
                } catch (e) {
                    // Ignore errors for individual keys
                }
            });
        } catch (e) {
            debugWarn('[useAuth] Failed to clear auth-related localStorage:', e);
        }
    }, []);

    const updateUserTeam = useCallback((teamUpdate: (team: TeamModel | undefined) => TeamModel | undefined) => {
        setState(prev => {
            if (!prev.user) return prev;

            const updatedTeam = teamUpdate(prev.user.team);
            const updatedUser: CurrentUserResponse = {
                ...prev.user,
                team: updatedTeam,
            };

            const newState = {
                ...prev,
                user: updatedUser,
            };

            // Persist to sessionStorage
            try {
                sessionStorage.setItem('auth_state', JSON.stringify({
                    isAuthenticated: newState.isAuthenticated,
                    user: newState.user,
                }));
            } catch (e) {
                debugWarn('[useAuth] Failed to persist auth state:', e);
            }

            return newState;
        });
    }, []);

    const switchTeam = useCallback(async (teamId: string) => {
        try {
            const response = await authApiService.switchTeam(teamId);
            // Server re-issued cookies; update local state with the new active team
            setState(prev => {
                if (!prev.user) return prev;
                const updatedUser: CurrentUserResponse = {
                    ...prev.user,
                    teamId: response.data.team?.teamId,
                    team: response.data.team,
                };
                const newState = { ...prev, user: updatedUser };
                try {
                    sessionStorage.setItem('auth_state', JSON.stringify({
                        isAuthenticated: newState.isAuthenticated,
                        user: newState.user,
                    }));
                } catch (e) {
                    debugWarn('[useAuth] Failed to persist auth state after team switch:', e);
                }
                return newState;
            });
        } catch (error) {
            const authError = error as AuthError;
            setError(authError.message || 'Team switch failed');
            throw error;
        }
    }, [setError]);

    const refreshTokenAction = useCallback(async () => {
        try {
            // With HTTP-only cookies, we don't have access to the refresh token
            // The server will use the refresh token from the HTTP-only cookie
            const response: AuthApiResponse<RefreshTokenResponse> = await authApiService.refreshToken('http-only-cookie');

            // Tokens are stored in HTTP-only cookies by the server
            const mockTokens: AuthTokens = {
                accessToken: 'http-only-cookie', // Actual token is in HTTP-only cookie
                refreshToken: 'http-only-cookie', // Actual token is in HTTP-only cookie
                accessTokenExpiresAt: response.data.accessTokenExpiresAt,
                refreshTokenExpiresAt: response.data.refreshTokenExpiresAt || '',
            };

            const user: CurrentUserResponse = {
                username: response.data.username || '',
                role: response.data.role || '',
                userId: response.data.userId,
                emailAddress: response.data.username || '',
                firstName: response.data.firstName || '',
                lastName: response.data.lastName || '',
                fullName: `${response.data.firstName || ''} ${response.data.lastName || ''}`.trim(),
                businessRole: response.data.businessRole || '',
                status: response.data.status,
                title: response.data.title,
                teamId: response.data.team?.teamId,
                team: response.data.team,
                defaultExpenseTemplateId: response.data.defaultExpenseTemplateId,
                claims: [], // Will be populated by getCurrentUser
            };

            setAuthData({ user, tokens: mockTokens });

            // Schedule next token refresh
            scheduleTokenRefresh(response.data.accessTokenExpiresAt);

        } catch (error) {
            const authError = error as AuthError;
            setError(authError.message || 'Token refresh failed');

            // If refresh fails, logout the user
            clearAuthData();
            authApiService.clearAuthCookies();

            throw error;
        }
    }, [setAuthData, setError, clearAuthData]);

    const scheduleTokenRefresh = useCallback((expiresAt: string) => {
        // Clear existing timeout
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        const expirationTime = new Date(expiresAt).getTime();
        const currentTime = Date.now();
        const timeUntilExpiry = expirationTime - currentTime;

        // Refresh token 5 minutes before expiry
        const refreshTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 60000); // At least 1 minute

        refreshTimeoutRef.current = setTimeout(() => {
            refreshTokenAction();
        }, refreshTime);
    }, [refreshTokenAction]);

    const login = useCallback(async (credentials: LoginRequest) => {
        setLoading(true);
        setError(null);

        try {
            const response: AuthApiResponse<LoginResponse> = await authApiService.login(credentials);

            // With HTTP-only cookies, tokens are stored in cookies by the server
            // We don't have access to them in JavaScript, but we know they exist
            const mockTokens: AuthTokens = {
                accessToken: 'http-only-cookie', // Actual token is in HTTP-only cookie
                refreshToken: 'http-only-cookie', // Actual token is in HTTP-only cookie
                accessTokenExpiresAt: response.data.accessTokenExpiresAt,
                refreshTokenExpiresAt: response.data.refreshTokenExpiresAt || '',
            };

            const user: CurrentUserResponse = {
                username: response.data.username || '',
                role: response.data.role || '',
                userId: response.data.userId,
                emailAddress: response.data.username || '',
                firstName: response.data.firstName || '',
                lastName: response.data.lastName || '',
                fullName: `${response.data.firstName || ''} ${response.data.lastName || ''}`.trim(),
                businessRole: response.data.businessRole || '',
                status: response.data.status,
                title: response.data.title,
                teamId: response.data.team?.teamId,
                team: response.data.team,
                defaultExpenseTemplateId: response.data.defaultExpenseTemplateId,
                claims: [], // Will be populated by getCurrentUser
            };

            setAuthData({ user, tokens: mockTokens });

            // Schedule token refresh based on expiration time
            scheduleTokenRefresh(response.data.accessTokenExpiresAt);

        } catch (error) {
            const authError = error as AuthError;
            setError(authError.message || 'Login failed');
            throw error;
        }
    }, [setLoading, setError, setAuthData, scheduleTokenRefresh]);

    const logout = useCallback(async () => {
        setLoading(true);
        setIsLoggingOut(true);
        setError(null);

        try {
            // Add a delay to ensure the spinner is visible
            await new Promise(resolve => setTimeout(resolve, 150));

            // Call logout API to clear HTTP-only cookies on server
            await authApiService.logout();
        } catch (error) {
            // Continue with logout even if API call fails
            // HTTP-only cookies will be cleared by the server if the request succeeds
            debugWarn('Logout API call failed:', error);
        } finally {
            // Clear refresh timeout
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
                refreshTimeoutRef.current = null;
            }

            // Reset module-level auth check ref
            moduleHasCheckedAuthRef.current = false;

            // Reset instance-level refs
            hasCheckedAuthRef.current = false;
            checkInProgressRef.current = false;
            lastValidatedStateRef.current = null;

            // Note: authApiService.clearAuthCookies() is a no-op for HTTP-only cookies
            // The server handles cookie clearing via the logout endpoint
            authApiService.clearAuthCookies();

            // Clear all React state and storage regardless of API response
            // This will set loading to false, so do it last
            clearAuthData();

            // Clear logout state after everything is done
            setIsLoggingOut(false);
        }
    }, [setLoading, setError, clearAuthData]);


    const getCurrentUser = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response: AuthApiResponse<CurrentUserResponse> = await authApiService.getCurrentUser();

            setState(prev => ({
                ...prev,
                user: response.data,
                loading: false,
                error: null,
            }));

        } catch (error) {
            const authError = error as AuthError;
            setError(authError.message || 'Failed to get current user');
            throw error;
        }
    }, [setLoading, setError]);

    const checkAuthStatus = useCallback(async () => {
        if (checkInProgressRef.current) {
            // debugLog('[useAuth] Auth check already in progress, skipping');
            return;
        }

        checkInProgressRef.current = true;
        setLoading(true);
        setError(null);

        try {
            // With HTTP-only cookies, we can't read tokens directly
            // Instead, we make an API call to check if we're authenticated
            const response: AuthApiResponse<CurrentUserResponse> = await authApiService.getCurrentUser();

            // If we get here, the user is authenticated
            // We don't have access to the actual tokens, but we know they exist in cookies
            const mockTokens: AuthTokens = {
                accessToken: 'http-only-cookie', // Placeholder - actual token is in HTTP-only cookie
                refreshToken: 'http-only-cookie', // Placeholder - actual token is in HTTP-only cookie
                accessTokenExpiresAt: '', // We don't know the exact expiration
                refreshTokenExpiresAt: '', // We don't know the exact expiration
            };

            // Update state first, then reset the progress flag
            setAuthData({ user: response.data, tokens: mockTokens });
            hasCheckedAuthRef.current = true;
            // debugLog('[useAuth] Authentication successful, user:', response.data.username);

        } catch (error) {
            // API call failed - user is not authenticated
            // This could be 401 (unauthorized) or 403 (forbidden) or network error
            const authError = error as any;
            const status = authError?.status;

            // If it's an auth error (401/403), force logout immediately
            if (status === 401 || status === 403) {
                debugWarn('[useAuth] Token validation failed - forcing logout', { status });
                clearAuthData();
                authApiService.clearAuthCookies();
                // Redirect to login by setting isAuthenticated to false
                // The ProtectedRoute will handle the redirect
            } else {
                // For other errors (network, etc.), clear auth but don't force redirect
                // as it might be a temporary network issue
                clearAuthData();
            }

            hasCheckedAuthRef.current = true;
            setLoading(false);
        } finally {
            // Always reset the progress flag after state updates have been queued
            // Use setTimeout to ensure state updates have been processed
            setTimeout(() => {
                checkInProgressRef.current = false;
            }, 0);
        }
    }, [setLoading, setError, setAuthData, clearAuthData]);

    // Check auth status on mount - but only if we don't already have auth data
    useEffect(() => {
        // Skip auth checks on public routes (reset password, unsubscribe)
        // Note: /auth/login is NOT in this list so it can auto-login if cookie exists
        const currentPath = window.location.pathname;
        const publicRoutes = ['/auth/reset-password', '/auth/unsubscribe', '/auth/signup', '/auth/register', '/client/expenses'];
        const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));

        if (isPublicRoute) {
            // On public routes, just set loading to false and skip auth checks
            if (state.loading) {
                setState(prev => ({ ...prev, loading: false }));
            }
            hasCheckedAuthRef.current = true;
            return;
        }

        // Check sessionStorage to see if we've already authenticated in this session
        const sessionAuth = (() => {
            try {
                const saved = sessionStorage.getItem('auth_state');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    return parsed.isAuthenticated && parsed.user ? parsed : null;
                }
            } catch (e) {
                // Ignore
            }
            return null;
        })();

        // Skip if we've already checked auth (successfully or failed)
        if (hasCheckedAuthRef.current || moduleHasCheckedAuthRef.current) {
            // debugLog('[useAuth] Skipping auth check - already checked', { hasCheckedRef: hasCheckedAuthRef.current, moduleChecked: moduleHasCheckedAuthRef.current });
            // If we have session auth but state is reset, restore it
            if (sessionAuth && (!state.isAuthenticated || !state.user)) {
                // debugLog('[useAuth] Restoring auth state from session');
                setAuthData({
                    user: sessionAuth.user,
                    tokens: {
                        accessToken: 'http-only-cookie',
                        refreshToken: 'http-only-cookie',
                        accessTokenExpiresAt: '',
                        refreshTokenExpiresAt: '',
                    }
                });
            }
            return;
        }

        // Skip if check is already in progress
        if (checkInProgressRef.current) {
            // debugLog('[useAuth] Skipping auth check - check in progress');
            return;
        }

        // If user is already authenticated, verify tokens are still valid
        // Even if marked as authenticated, we need to verify the HTTP-only cookies are still valid
        if (state.isAuthenticated || state.user) {
            // Check if we have valid token placeholders (HTTP-only cookies are in use)
            // If tokens are null (not 'http-only-cookie'), we should verify authentication
            const hasTokenPlaceholder = state.accessToken === 'http-only-cookie' || state.refreshToken === 'http-only-cookie';

            if (!hasTokenPlaceholder && (state.accessToken === null || state.refreshToken === null)) {
                // Tokens are missing - force logout
                debugWarn('[useAuth] User marked as authenticated but tokens are missing - forcing logout');
                clearAuthData();
                authApiService.clearAuthCookies();
                hasCheckedAuthRef.current = true;
                moduleHasCheckedAuthRef.current = false;
                return;
            }

            // If we have token placeholders, verify they're still valid by making an API call
            // This ensures the HTTP-only cookies haven't expired
            // Only verify if we haven't already validated this state
            const stateSignature = `${state.isAuthenticated}-${state.user?.userId}-${state.accessToken}-${state.refreshToken}`;
            if (hasTokenPlaceholder && !checkInProgressRef.current && lastValidatedStateRef.current !== stateSignature) {
                // Mark this state as being validated
                lastValidatedStateRef.current = stateSignature;

                // Verify token is still valid in the background
                checkAuthStatus()
                    .then(() => {
                        // Update signature after successful validation
                        const currentSignature = `${state.isAuthenticated}-${state.user?.userId}-${state.accessToken}-${state.refreshToken}`;
                        if (currentSignature === stateSignature) {
                            lastValidatedStateRef.current = stateSignature;
                        }
                    })
                    .catch(err => {
                        // On error, reset so we can retry
                        lastValidatedStateRef.current = null;
                        debugError('[useAuth] Background token verification failed:', err);
                    });
            }

            hasCheckedAuthRef.current = true;
            moduleHasCheckedAuthRef.current = true;
            // Ensure loading is false if we're already authenticated
            if (state.loading) {
                setState(prev => ({ ...prev, loading: false }));
            }
            return;
        }

        // If we have session auth, verify it's still valid by making an API call
        // This ensures the token in the cookie hasn't expired
        // Keep loading true during verification to prevent redirect to login
        if (sessionAuth) {
            // debugLog('[useAuth] Found session auth, verifying token validity');
            // Optimistically restore user data for better UX, but keep loading true
            // until we verify the token is still valid
            setState(prev => ({
                ...prev,
                user: sessionAuth.user,
                isAuthenticated: true,
                loading: true, // Keep loading true during verification
            }));
            // Verify token is still valid
            checkAuthStatus().catch(err => {
                debugError('[useAuth] Token verification failed:', err);
                // Ensure loading is set to false even on error
                checkInProgressRef.current = false;
                setLoading(false);
            });
            return;
        }

        // Only check auth status if we're not already authenticated
        // This prevents unnecessary API calls and race conditions
        // debugLog('[useAuth] Will check auth status');
        // Remove timeout delay - check immediately to avoid showing login screen
        checkAuthStatus().catch(err => {
            debugError('[useAuth] Auth check failed with unhandled error:', err);
            // Ensure loading is set to false even on error
            checkInProgressRef.current = false;
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.isAuthenticated, state.user]); // Intentionally exclude checkAuthStatus to prevent re-runs

    // Immediate validation: If user is marked as authenticated but tokens are null, force logout
    useEffect(() => {
        // Skip validation on public routes (reset password, unsubscribe, signup)
        // Note: /auth/login is NOT in this list so it can auto-login if cookie exists
        const currentPath = window.location.pathname;
        const publicRoutes = ['/auth/reset-password', '/auth/unsubscribe', '/auth/signup', '/auth/register', '/client/expenses'];
        const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));

        if (isPublicRoute) {
            return;
        }

        if (state.isAuthenticated && state.user) {
            // Check if tokens are actually null (not just 'http-only-cookie' placeholder)
            const tokensAreNull = state.accessToken === null || state.refreshToken === null;
            const hasTokenPlaceholder = state.accessToken === 'http-only-cookie' || state.refreshToken === 'http-only-cookie';

            // If tokens are null and we don't have placeholders, tokens are missing - force logout
            if (tokensAreNull && !hasTokenPlaceholder) {
                debugWarn('[useAuth] User authenticated but tokens are missing - forcing immediate logout');
                clearAuthData();
                authApiService.clearAuthCookies();
                return;
            }

            // If we have token placeholders, verify they're still valid
            // Create a state signature to track if we've already validated this exact state
            const stateSignature = `${state.isAuthenticated}-${state.user?.userId}-${state.accessToken}-${state.refreshToken}`;

            if (hasTokenPlaceholder && !checkInProgressRef.current && lastValidatedStateRef.current !== stateSignature) {
                // Mark this state as being validated
                lastValidatedStateRef.current = stateSignature;

                // Verify token is still valid by making an API call
                checkAuthStatus()
                    .then(() => {
                        // Update the signature after successful validation to prevent re-validation
                        // Only if state hasn't changed
                        const currentSignature = `${state.isAuthenticated}-${state.user?.userId}-${state.accessToken}-${state.refreshToken}`;
                        if (currentSignature === stateSignature) {
                            lastValidatedStateRef.current = stateSignature;
                        }
                    })
                    .catch(err => {
                        // On error, reset the signature so we can retry
                        lastValidatedStateRef.current = null;
                        debugError('[useAuth] Token validation failed:', err);
                    });
            }
        } else {
            // Reset validation tracking when user is not authenticated
            lastValidatedStateRef.current = null;
        }
    }, [state.isAuthenticated, state.user, state.accessToken, state.refreshToken, clearAuthData]); // Removed checkAuthStatus from deps - it's stable

    // Periodic token validation check - verify tokens are still valid every 5 minutes
    useEffect(() => {
        if (!state.isAuthenticated || !state.user) {
            return;
        }

        // Set up periodic validation check
        const validationInterval = setInterval(() => {
            // Skip if we're on a public route (reset password, unsubscribe)
            // Note: /auth/login is NOT in this list so it can auto-login if cookie exists
            const currentPath = window.location.pathname;
            const publicRoutes = ['/auth/reset-password', '/auth/unsubscribe', '/auth/register', '/client/expenses'];
            const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));

            if (isPublicRoute) {
                return;
            }

            // Only check if we're not already checking and user is authenticated
            if (!checkInProgressRef.current && state.isAuthenticated) {
                // Verify token is still valid by making a lightweight API call
                checkAuthStatus().catch(err => {
                    debugError('[useAuth] Periodic token validation failed:', err);
                });
            }
        }, 5 * 60 * 1000); // Check every 5 minutes

        return () => {
            clearInterval(validationInterval);
        };
    }, [state.isAuthenticated, state.user, checkAuthStatus]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, []);

    return {
        ...state,
        isLoggingOut,
        login,
        logout,
        refreshTokenAction,
        getCurrentUser,
        clearError,
        checkAuthStatus,
        updateUserTeam,
        switchTeam,
    };
}

